import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import klubService from '../../services/klubService';
import { notify } from '../../components/common/AppToast';
import { ROUTES } from '../../config/routes';
import { goBackOr } from '../../utils/historyBack';
import { AppButton, AppFormField, AppInput, AppSpinner } from '../../components/ui';
import { AppFilterCommercialSelect, AppFilterSelect } from '../../components/filters';
import KlubDashboardStatCard from '../../components/klub/KlubDashboardStatCard';
import KlubCeWorkList from '../../components/klub/KlubCeWorkList';
import {
  CE_DASHBOARD_ETAPES,
  SALARIES_RANGE_OPTIONS,
  ceDashboardEtape,
  matchesSalariesRange,
} from '../../utils/klubUtils';
import '../../styles/klub-dashboard.scss';

const dashboardUrl = (etape) => (
  etape ? `${ROUTES.klub.ce}?etape=${encodeURIComponent(etape)}` : ROUTES.klub.ce
);

const KlubCeDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const etape = searchParams.get('etape') || '';
  const [scrolled, setScrolled] = useState(false);
  const [items, setItems] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [commercialIds, setCommercialIds] = useState([]);
  const [salariesRanges, setSalariesRanges] = useState([]);

  const loadList = ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    return klubService.listCe({ search, limit: 1000 })
      .then((res) => setItems(res.ce ?? []))
      .catch((err) => notify.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = window.scrollY > 16;
        setScrolled((prev) => (prev === next ? prev : next));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    klubService.listCommerciaux()
      .then(setCommerciaux)
      .catch(() => setCommerciaux([]));
  }, []);

  useEffect(() => {
    loadList();
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const commercialOptions = useMemo(
    () => commerciaux.map((commercial) => ({ value: String(commercial.id), label: commercial.nom })),
    [commerciaux],
  );

  const filtered = useMemo(() => items.filter((ce) => {
    if (commercialIds.length > 0) {
      const id = ce.commercial?.id != null ? String(ce.commercial.id) : '';
      if (!commercialIds.includes(id)) return false;
    }
    return matchesSalariesRange(ce.nombre_salaries, salariesRanges);
  }), [items, commercialIds, salariesRanges]);

  const cardStats = useMemo(() => {
    const stats = Object.fromEntries(CE_DASHBOARD_ETAPES.map((card) => [card.code, { count: 0, salaries: 0 }]));
    filtered.forEach((ce) => {
      const code = ceDashboardEtape(ce);
      if (!stats[code]) return;
      stats[code].count += 1;
      stats[code].salaries += Number(ce.nombre_salaries) || 0;
    });
    return stats;
  }, [filtered]);

  const listed = useMemo(
    () => (etape ? filtered.filter((ce) => ceDashboardEtape(ce) === etape) : filtered),
    [filtered, etape],
  );

  return (
    <MasterLayout>
      <Breadcrumb title="CE Klub — Dashboard" hideHeading />

      <div className="klub-dashboard">
        <div className={`klub-dashboard__pin${scrolled ? ' is-scrolled' : ''}`}>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
            <div>
              <h4 className="fw-semibold mb-1">CE Klub</h4>
              <p className="text-secondary-light mb-0">Suivi listing et remise des cartes</p>
            </div>
            <AppButton
              size="sm"
              variant="outline-secondary"
              icon="mdi:arrow-left"
              onClick={() => goBackOr(navigate, ROUTES.klub.dashboard)}
            >
              Retour
            </AppButton>
          </div>

          {loading && items.length === 0 ? (
            <AppSpinner />
          ) : (
            <>
              <div className="klub-dashboard__stats klub-dashboard__stats--ce">
                {CE_DASHBOARD_ETAPES.map((card) => (
                  <KlubDashboardStatCard
                    key={card.code}
                    to={dashboardUrl(etape === card.code ? '' : card.code)}
                    active={etape === card.code}
                    color={card.color}
                    icon={card.icon}
                    label={card.label}
                    hint={card.hint}
                    actionFirst={card.code !== 'refus'}
                    value={cardStats[card.code]?.count ?? 0}
                    secondaryValue={cardStats[card.code]?.salaries ?? 0}
                  />
                ))}
              </div>

              <div className="row g-3 align-items-end">
                <div className="col-12 col-lg-4">
                  <AppFormField label="Recherche client" htmlFor="ce-dashboard-search" className="mb-0">
                    <AppInput
                      id="ce-dashboard-search"
                      value={searchInput}
                      placeholder="Entreprise, contact…"
                      onChange={(event) => setSearchInput(event.target.value)}
                    />
                  </AppFormField>
                </div>
                <AppFilterCommercialSelect
                  id="ce-dashboard-commercial"
                  label="Commercial"
                  col="col-12 col-md-6 col-lg-4"
                  multiple
                  value={commercialIds}
                  onChange={setCommercialIds}
                  options={commercialOptions}
                  placeholder="Tous les commerciaux"
                />
                <AppFilterSelect
                  id="ce-dashboard-salaries"
                  label="Nb de salariés"
                  col="col-12 col-md-6 col-lg-4"
                  value={salariesRanges}
                  onChange={setSalariesRanges}
                  options={SALARIES_RANGE_OPTIONS.map(({ value, label }) => ({ value, label }))}
                  placeholder="Toutes les fourchettes"
                />
              </div>
            </>
          )}
        </div>

        {!(loading && items.length === 0) && (
          <KlubCeWorkList
            items={listed}
            commerciaux={commerciaux}
            loading={loading && items.length === 0}
            onChanged={() => loadList({ silent: true })}
          />
        )}
      </div>
    </MasterLayout>
  );
};

export default KlubCeDashboardPage;
